import { forwardRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

/**
 * Returns the current locale ('ar' | 'en') extracted from the URL pathname.
 * Falls back to 'ar' if the URL is not yet prefixed.
 */
export const getLocaleFromPath = (pathname) => {
  if (!pathname) return 'ar';
  const seg = pathname.split('/').filter(Boolean)[0];
  return seg === 'en' || seg === 'ar' ? seg : 'ar';
};

/**
 * Hook returning a function that prefixes an internal path with the active locale.
 *   const lp = useLocalePath();
 *   <Link to={lp('/features')} />
 *
 * Special cases:
 *   - path '/dashboard' is NEVER prefixed (it's the mock app shell).
 *   - paths already starting with '/ar/' or '/en/' are passed through unchanged.
 *   - external URLs (http://, https://, mailto:, #) are passed through unchanged.
 */
export const useLocalePath = () => {
  const { pathname } = useLocation();
  const lang = getLocaleFromPath(pathname);

  return (to) => {
    if (typeof to !== 'string') return to;
    if (to.startsWith('http') || to.startsWith('mailto:') || to.startsWith('#') || to.startsWith('tel:')) {
      return to;
    }
    if (to === '/dashboard' || to.startsWith('/dashboard/')) return to;
    if (to.startsWith('/ar/') || to === '/ar' || to.startsWith('/en/') || to === '/en') return to;
    // Normalize: ensure leading slash
    const normalized = to.startsWith('/') ? to : `/${to}`;
    return `/${lang}${normalized === '/' ? '' : normalized}`;
  };
};

/**
 * <Link>-compatible component that auto-prefixes its `to` with the current locale.
 * Drop-in replacement for react-router-dom's Link in app code.
 */
const LocaleLink = forwardRef(({ to, ...rest }, ref) => {
  const lp = useLocalePath();
  return <Link ref={ref} to={lp(to)} {...rest} />;
});

LocaleLink.displayName = 'LocaleLink';
export default LocaleLink;
